/**
 * Abrir e fechar o **modal do Negócio** (história 11.4) — o Negócio sobre a tela de origem.
 *
 * A espinha quer o Negócio em modal, empilhado **sobre** a tela de onde ele foi aberto (Funil,
 * Hoje, Tarefas, WhatsApp, Originados, listas de Relatórios), com a URL `/negocios/:id`. O desenho
 * é o do "modal roteado" com **máscara de rota** do TanStack Router:
 *
 * - **Abrir de dentro do app** acrescenta `?negocio=<id>` à tela atual (a tela de fundo continua
 *   montada, com o estado dela) e **mascara** a barra de endereço como `/negocios/<id>`. O layout
 *   (`rotas/aplicacao.tsx`) lê o parâmetro e desenha o modal por cima do `<Outlet/>`.
 * - **Link colado / recarregar**: o endereço real é `/negocios/<id>`, e a rota dele desenha a tela
 *   Hoje de fundo com o modal por cima (`rotas/negocio.tsx`).
 * - **Fechar** volta à tela de origem sem o parâmetro (`replace`: o fechar não empilha histórico).
 *
 * O link é um `<a href="/negocios/<id>">` de verdade: "abrir em nova aba" e o clique do meio levam
 * ao endereço real (o modal sobre Hoje), e o clique simples é interceptado para abrir sobre a tela
 * atual. "Clique no nome abre" — só o nome é link (Interaction Primitives).
 */

import { useLocation, useNavigate } from '@tanstack/react-router'
import { type MouseEvent, type ReactNode, useCallback } from 'react'

/** O parâmetro de busca que carrega o Negócio aberto sobre a tela de origem. */
export const PARAMETRO_DO_NEGOCIO = 'negocio'

/** O que o layout aceita na busca de toda tela interna. */
export interface BuscaDoModalDoNegocio {
  /** O `id` do Negócio aberto em modal sobre esta tela. Ausente é "nenhum". */
  negocio?: string
}

/** O `id` do Negócio é um UUID (AD-12); qualquer outra coisa em `?negocio=` é descartada. */
const FORMATO_DE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** O `validateSearch` do layout: só um UUID sobrevive em `?negocio=`. */
export function validarBuscaDoModal(parametros: Record<string, unknown>): BuscaDoModalDoNegocio {
  const bruto = parametros[PARAMETRO_DO_NEGOCIO]
  const id = typeof bruto === 'string' ? bruto.trim() : ''
  return { negocio: FORMATO_DE_UUID.test(id) ? id : undefined }
}

/** Se o endereço **real** já é o de um Negócio (link colado) — ali não se empilha outro modal. */
export function ehEnderecoDeNegocio(caminho: string): boolean {
  return /^\/negocios\/[^/]+$/.test(caminho)
}

/** O endereço real do Negócio — o que a barra mostra e o que "abrir em nova aba" abre. */
export function enderecoDoNegocio(negocioId: string): string {
  return `/negocios/${encodeURIComponent(negocioId)}`
}

/** Abre o Negócio em modal sobre a tela atual, com a barra mascarada como `/negocios/<id>`. */
export function useAbrirNegocio() {
  const navegar = useNavigate()
  const caminho = useLocation({ select: (local) => local.pathname })
  return useCallback(
    (negocioId: string) => {
      /* Sobre o endereço real de outro Negócio (link colado) não se empilha um segundo modal:
         vai-se para o endereço do novo. */
      if (ehEnderecoDeNegocio(caminho)) {
        void navegar({ to: '/negocios/$negocioId', params: { negocioId } })
        return
      }
      void navegar({
        to: '.',
        search: (anterior) => ({ ...anterior, negocio: negocioId }),
        mask: { to: '/negocios/$negocioId', params: { negocioId } },
      })
    },
    [navegar, caminho],
  )
}

/** Fecha o modal aberto sobre a tela atual — a tela de origem fica, sem o parâmetro. */
export function useFecharNegocioDaBusca() {
  const navegar = useNavigate()
  return useCallback(() => {
    void navegar({
      to: '.',
      search: (anterior) => ({ ...anterior, negocio: undefined }),
      replace: true,
    })
  }, [navegar])
}

export interface PropsDoLinkDoNegocio {
  negocioId: string
  className?: string
  children: ReactNode
  'aria-label'?: string
}

/**
 * O nome que abre o Negócio. Clique simples abre o modal sobre a tela atual; clique com
 * modificador (nova aba, nova janela) segue o `href` real.
 */
export function LinkDoNegocio({
  negocioId,
  className,
  children,
  'aria-label': rotulo,
}: PropsDoLinkDoNegocio) {
  const abrir = useAbrirNegocio()
  const aoClicar = (evento: MouseEvent<HTMLAnchorElement>) => {
    if (
      evento.defaultPrevented ||
      evento.button !== 0 ||
      evento.metaKey ||
      evento.ctrlKey ||
      evento.shiftKey ||
      evento.altKey
    ) {
      return
    }
    evento.preventDefault()
    abrir(negocioId)
  }
  return (
    <a
      href={enderecoDoNegocio(negocioId)}
      className={className}
      aria-label={rotulo}
      onClick={aoClicar}
      /* O arrastar do card (Funil) não pode nascer do link: o nome é o alvo do clique. */
      draggable={false}
    >
      {children}
    </a>
  )
}
